import * as Mui from '@material-ui/core';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from "react-toastify";
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeGrid as Grid, areEqual } from 'react-window';
import { CheckboxTable } from '../../../../../components/checkbox/checkbox-table';
import { IconBuilding, IconCamera } from '../../../../../components/svg';
import ToastElement from '../../../../../components/toast';
import { highlightSearchTerm } from '../../../../../helpers/price-rule';
import { useTheme } from '../../../../../ThemeContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NoDataFound } from '../../../canteen-management/canteen/view-components/price-rule/components/no-data-found';
import { bulkCategoryProductsUpdate, categoryProductsUpdate } from '../../../../../helpers/org-onboard';
import { InlineMultiSelect } from '../../../../../components/form/inline-multiselect';
import { Search } from '@material-ui/icons';

export const ManageCatelogProducts = memo(({ formikProps, searchQuery }) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { orgwiseProductsLoading, organizations } = useSelector(state => state.organization);

    const [filteredOrganizations, setFilteredOrganizations] = useState([]);

    const productCatalogRef = useRef(null);

    const [isAllProductsSelected, setIsAllProductsSelected] = useState(false);

    const isProductSelected = productCatalogRef?.current?.allProductsSelected() || false;

    useEffect(() => {
        setIsAllProductsSelected(productCatalogRef?.current?.allProductsSelected() || false);
    }, [searchQuery, filteredOrganizations, isProductSelected])


    const selectedOrganizations = useMemo(() => {
        const activeOrganizations = organizations?.filter(organization => organization?.statusConfigChoice?.configChoice === 'active');
        return formikProps.values.organizations.map(org => {
            const activeOrg = activeOrganizations.find(activeOrg => activeOrg.uuid === org);
            return {
                label: activeOrg.name,
                value: activeOrg.uuid
            }
        }).filter(org => org !== undefined);
    }, [formikProps.values.organizations, organizations]);

    useEffect(() => {
        setFilteredOrganizations(prev => formikProps.values.organizations.filter(org => prev.includes(org)));
    }, [formikProps.values.organizations])

    const isAllOrganizationsSelected = useMemo(() => {
        return selectedOrganizations.every(org => filteredOrganizations.includes(org.value));
    }, [selectedOrganizations, filteredOrganizations]);

    return <Mui.Box style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Mui.Box
            className='d-flex justify-content-between align-items-center pl-1 pr-1 pt-2 pb-2'
        >
            <Mui.Box
                className='font-weight-normal text-color-primary'
                style={{
                    fontSize: '1.4rem',
                    border: `1px solid ${theme === 'dark' ? '#F8F8F9' : '#3121B2'}`,
                    borderRadius: '0.8rem',
                    padding: '1rem 1rem',
                    background: theme === 'dark' ? 'transparent' : '#B7C1ED',
                }}
            >
                {t("ORGANIZATION.ONBOARD.CATALOG.ACTIVE_MENU")}: {formikProps.values.selectedSubCategory?.name}
            </Mui.Box>
            <Mui.Box style={{ flex: 1 }} className='d-flex justify-content-end align-items-center' gridGap={15}>
                <InlineMultiSelect
                    data={isAllOrganizationsSelected ? selectedOrganizations : [{ label: 'All', value: 'all' }, ...selectedOrganizations]}
                    value={filteredOrganizations}
                    onChange={(value) => {
                        if (value.includes('all')) {
                            setFilteredOrganizations(selectedOrganizations.map(org => org.value));
                            return;
                        }
                        else {
                            setFilteredOrganizations(value);
                        }
                    }}
                    placeholder={t("ORGANIZATION.ONBOARD.CATALOG.SELECT_ORGS")}
                    searchable
                    nothingFound={t("ORGANIZATION.ONBOARD.CATALOG.NO_ORGS")}
                    clearable
                    icon={<Search />}
                    rightSectionWidth="auto"
                    style={{ width: '50%' }}
                />
                {((searchQuery.length > 0 || filteredOrganizations.length > 0) && formikProps.values.selectedSubCategory) && <Mui.Typography
                    component="h6"
                    variant="h6"
                    className="font-weight-medium text-color-primary pointer"
                    onClick={() => {
                        productCatalogRef.current.selectFilteredProducts();
                    }}
                >
                    {(isAllProductsSelected) ? t("ORGANIZATION.ONBOARD.CATALOG.DESELECT_ALL") : t("ORGANIZATION.ONBOARD.CATALOG.SELECT_ALL")}
                </Mui.Typography>}
            </Mui.Box>

        </Mui.Box>
        <div style={{ flex: '1 1 auto', height: '100%', width: '100%', overflow: 'hidden' }}>
            {orgwiseProductsLoading ? <Mui.Box className='d-flex justify-content-center align-items-center' style={{ height: '100%', width: '100%' }}> <Mui.CircularProgress color='primary' /> </Mui.Box> :
                <CatelogProducts
                    formikProps={formikProps}
                    searchQuery={searchQuery}
                    ref={productCatalogRef}
                    filteredOrganizations={filteredOrganizations}
                    selectedOrganizations={selectedOrganizations}
                />

            }
        </div>
    </Mui.Box>

})


const CatelogProducts = memo(forwardRef(({ formikProps, searchQuery, filteredOrganizations, selectedOrganizations }, ref) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { i18n } = useTranslation();
    const { orgwiseProducts } = useSelector(state => state.organization);

    const { selectedSubCategory, categories } = formikProps.values;


    //products from the orgwise products
    const products = useMemo(() => {
        if (!orgwiseProducts) return [];
        const selectedOrgUUIDs = formikProps.values.organizations || [];
        let filteredOrgwiseProducts = Object.entries(orgwiseProducts)
            .filter(([orgId]) => selectedOrgUUIDs.includes(orgId))
            .flatMap(([orgId, productsArr]) =>
                (productsArr || []).map(product => ({
                    ...product,
                    orgId
                }))
            );
        if (filteredOrganizations.length > 0) {
            filteredOrgwiseProducts = filteredOrgwiseProducts.filter(item => filteredOrganizations.some(org => org === item.orgId));
        }
        return filteredOrgwiseProducts;
    }, [orgwiseProducts, formikProps.values.organizations, filteredOrganizations]);




    //active category products
    const activeCategoryProducts = useMemo(() =>
        categories
            .find(cat => cat.parentCategory.id === selectedSubCategory?.parentCategory?.id)
            ?.subCategories.find(subCat => subCat.id === selectedSubCategory?.id)?.products || []
        , [categories, selectedSubCategory]);

    //products not to show in the grid
    const productNotToShow = useMemo(() => categories
        .map(cat => cat.subCategories.filter(subCat => subCat.id !== selectedSubCategory?.id).map(subCat => subCat.products).flat()).flat()
        , [categories, selectedSubCategory]);

    //filter products based on search query and product not to show
    const filteredProducts = useMemo(() =>
        products.filter(item => item.name?.[i18n.language].toLowerCase().includes(searchQuery.toLowerCase()) && !productNotToShow.some(product => product.uuid === item.uuid && product.orgId === item.orgId))
        , [searchQuery, productNotToShow, i18n.language, products]);


    //update product selection
    const updateProductSelection = useCallback((currProduct, parentCategoryId, subCategoryId) => {
        const updatedCategories = categoryProductsUpdate(categories, parentCategoryId, subCategoryId, currProduct);
        formikProps.setFieldValue('categories', [...updatedCategories])
    }, [categories, formikProps]);

    //update bulk product selection
    const updateBlukProductSelection = useCallback((bulkProducts, parentCategoryId, subCategoryId, isAllProductsSelected = false) => {
        const updatedCategories = bulkCategoryProductsUpdate(categories, parentCategoryId, subCategoryId, bulkProducts, isAllProductsSelected);
        formikProps.setFieldValue('categories', [...updatedCategories])
    }, [categories, formikProps]);

    //item data for the grid
    const itemData = useMemo(() => ({
        filteredProducts, activeCategoryProducts, updateProductSelection, selectedSubCategory, theme, searchQuery, selectedOrganizations
    }), [filteredProducts, activeCategoryProducts, selectedSubCategory, theme, searchQuery, updateProductSelection, selectedOrganizations]);


    //check if all products are selected
    const isAllProductsSelected = useMemo(() => {
        return filteredProducts.every(product => activeCategoryProducts.some(p => p.uuid === product.uuid && p.orgId === product.orgId));
    }, [filteredProducts, activeCategoryProducts]);

    useImperativeHandle(ref, () => {
        return {
            selectFilteredProducts: () => {
                if (isAllProductsSelected) {
                    updateBlukProductSelection(filteredProducts, selectedSubCategory.parentCategory.id, selectedSubCategory.id, true);
                }
                else {
                    updateBlukProductSelection(filteredProducts, selectedSubCategory.parentCategory.id, selectedSubCategory.id, false);
                }
            },
            allProductsSelected: () => { return isAllProductsSelected }
        }
    })

    if (filteredProducts.length === 0) {
        return <Mui.Box className='d-flex justify-content-center align-items-start' style={{ height: '100%', width: '100%' }}>
            <NoDataFound
                height="55vh"
                title={t("ORGANIZATION.ONBOARD.CATALOG.NO_PRODUCTS")}
                subtitle={t("ORGANIZATION.ONBOARD.CATALOG.NO_PRODUCTS_DESC")}
            />
        </Mui.Box>
    }


    return (
        <AutoSizer>
            {({ width, height }) => {
                if (width === 0 || height === 0) return null;
                const rowHeight = width < 400 ? 140 : 120;
                const columnCount = width < 600 ? 2 : 3;
                const rowCount = Math.ceil(filteredProducts.length / columnCount);
                return (
                    <Grid
                        columnCount={columnCount}
                        columnWidth={Math.floor(width / columnCount) - 10}
                        rowCount={rowCount}
                        rowHeight={rowHeight}
                        width={width}
                        height={height}
                        itemData={{ ...itemData, columnCount, width }}
                    >
                        {Cell}
                    </Grid>
                );
            }}
        </AutoSizer>

    );
}));





const Cell = memo(({ columnIndex, rowIndex, style, data }) => {
    const { t, i18n } = useTranslation();
    const {
        filteredProducts,
        activeCategoryProducts,
        updateProductSelection,
        selectedSubCategory,
        theme,
        searchQuery,
        columnCount,
        width,
        selectedOrganizations
    } = data;

    const index = rowIndex * columnCount + columnIndex;
    if (index >= filteredProducts.length) return null;

    const item = filteredProducts[index];
    const isSelected = activeCategoryProducts.some(product => product.uuid === item.uuid && product.orgId === item.orgId);

    const orgName = selectedOrganizations.find(org => org.value === item.orgId)?.label;
    const truncatedOrgName = orgName?.length > 20 ? orgName.substring(0, 20) + '...' : orgName;

    return (
        <div style={style}>
            <Mui.Box onClick={() => {
                if (!selectedSubCategory) {
                    toast.error(
                        <ToastElement type="error" message={t("ORGANIZATION.ONBOARD.CATALOG.SELECT_SUBCATEGORY_FIRST")} />,
                        { containerId: "default", position: "bottom-right" }
                    );
                    return
                }
                updateProductSelection(item, selectedSubCategory.parentCategory.id, selectedSubCategory.id)

            }}
                className='p-1 pointer d-flex justify-content-between'
                flexDirection='column'
                style={{
                    border: `1.5px solid ${isSelected ? '#079455' : theme === 'dark' ? '#37395B' : '#E9ECEF'}`,
                    borderRadius: '0.8rem', margin: '0.5rem', height: '95%',
                }}>
                <Mui.Box className='d-flex justify-content-between align-items-start'>
                    <Mui.Typography className='font-weight-medium' variant='h6' component={'h6'}>
                        {highlightSearchTerm(searchQuery, item.name?.[i18n.language], width < 400 ? 30 : 40)}
                    </Mui.Typography>
                    <CheckboxTable
                        checked={isSelected}
                        considerDarkMode={false}
                        color='#079455'
                    />
                </Mui.Box>
                <Mui.Box className='d-flex justify-content-between align-items-center mt-1'>
                    <Mui.Box className='d-flex align-items-center' gridGap={5}>
                        <IconBuilding width={15} height={15} stroke={theme === 'dark' ? '#F8F8F9' : '#3121B2'} />
                        <Mui.Typography className='font-weight-normal text-color-primary' variant='body2' component={'span'}>{truncatedOrgName}</Mui.Typography>
                    </Mui.Box>
                    <Mui.Box className='d-flex align-items-center' gridGap={5}>
                        <IconCamera width={15} height={15} fill='#079455' />
                        <Mui.Typography style={{ color: '#079455' }} className='font-weight-normal' variant='body2' component={'span'}>{item?.annotation_count}</Mui.Typography>
                    </Mui.Box>
                </Mui.Box>
            </Mui.Box>
        </div>
    );
}, areEqual);
